import { useFormikContext } from 'formik';
import { Pencil } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';

import { useToggle } from '@/hooks/use-toggle';
import { useCurrentUser } from '@/hooks/useCurrentUser';

import { useUpdateUserProfileMutation } from '@/query/user.query';

import type { UpdateUserProfileFormValues } from '@/schemas/user.schema';

import { UpdateProfileForm } from './profile-update-form';

import { UpdateProfileFormProvider } from '@/formik/user.formik';

const SubmitButton = ({ isLoading }: { isLoading: boolean }) => {
  const { isValid } = useFormikContext<UpdateUserProfileFormValues>();

  return (
    <Button type="submit" className="flex-1" disabled={isLoading || !isValid}>
      Save changes
    </Button>
  );
};

export const ProfileUpdate = () => {
  const { userData } = useCurrentUser();

  const { state, toggle } = useToggle();

  const mutation = useUpdateUserProfileMutation({
    onSuccess: () => {
      toggle();
    },
  });

  return (
    <Sheet open={state} onOpenChange={toggle}>
      <SheetTrigger asChild>
        <Button variant="outline">
          <Pencil className="h-4 w-4" />
          Edit profile
        </Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto p-0">
        <SheetHeader className="p-4 w-full relative">
          <SheetTitle>Edit profile</SheetTitle>
          <SheetDescription>
            Update your personal information and account settings.
          </SheetDescription>
        </SheetHeader>
        <UpdateProfileFormProvider onSubmit={mutation.mutate} initialValues={userData}>
          <UpdateProfileForm />

          <SheetFooter className="sticky bottom-0 bg-background border-t p-4 w-full flex flex-row gap-2">
            <Button variant="outline" onClick={toggle} type="button" className="flex-1">
              Cancel
            </Button>
            <SubmitButton isLoading={mutation.isPending} />
          </SheetFooter>
        </UpdateProfileFormProvider>
      </SheetContent>
    </Sheet>
  );
};
